/**
 * Schedules a callback during idle periods, or falls back to setTimeout.
 * @type {(callback: IdleRequestCallback, options?: IdleRequestOptions) => number}
 */
export const requestIdleCallback =
  typeof window.requestIdleCallback === 'function'
    ? (cb, opts) => window.requestIdleCallback(cb, opts)
    : (cb) => window.setTimeout(cb, 0);

/**
 * @typedef {{ [key: string]: string | undefined }} Headers
 */

/**
 * Creates a fetch configuration object for cart AJAX requests.
 * @param {'json' | 'javascript'} [type] The type of response to expect
 * @param {Object} [config] The config of the request
 * @param {BodyInit} [config.body] The body of the request
 * @param {Headers} [config.headers] The headers of the request
 * @returns {RequestInit} The fetch configuration object
 */
export function fetchConfig(type = 'json', config = {}) {
  /** @type {Headers} */
  const headers = {
    'Content-Type': 'application/json',
    Accept: `application/${type}`,
    ...config.headers,
  };

  if (type === 'javascript') {
    headers['X-Requested-With'] = 'XMLHttpRequest';
    delete headers['Content-Type'];
  }

  return {
    method: 'POST',
    headers: /** @type {HeadersInit} */ (headers),
    body: config.body,
  };
}

/**
 * Creates a debounced function that delays calling fn until after wait ms.
 * The returned function has a `.cancel()` method.
 *
 * @template {(...args: any[]) => any} T
 * @param {T} fn The function to debounce
 * @param {number} wait Milliseconds to wait before calling fn
 * @returns {T & { cancel(): void }}
 */
export function debounce(fn, wait) {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let timeout;

  /**
   * @this {any}
   * @param {...any} args
   */
  function debounced(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  }

  debounced.cancel = () => {
    clearTimeout(timeout);
  };

  return /** @type {T & { cancel(): void }} */ (debounced);
}

/**
 * Resolves when element animations finish.
 * @param {Element | Element[]} elements
 * @param {() => void} [callback]
 * @param {{ subtree?: boolean }} [options]
 * @returns {Promise<PromiseSettledResult<Animation>[]>}
 */
export function onAnimationEnd(
  elements,
  callback,
  options = { subtree: true }
) {
  const animations = Array.isArray(elements)
    ? elements.flatMap((element) => element.getAnimations(options))
    : elements.getAnimations(options);
  const animationPromises = animations.reduce((acc, animation) => {
    if (animation.timeline instanceof DocumentTimeline) {
      acc.push(animation.finished);
    }
    return acc;
  }, /** @type {Promise<Animation>[]} */ ([]));

  return Promise.allSettled(animationPromises).then((results) => {
    callback?.();
    return results;
  });
}

/**
 * @param {MouseEvent} event
 * @param {Element} element
 * @returns {boolean}
 */
export function isClickedOutside(event, element) {
  if (
    event.target instanceof HTMLDialogElement ||
    !(event.target instanceof Element)
  ) {
    return !isPointWithinElement(event.clientX, event.clientY, element);
  }
  return !element.contains(event.target);
}

/**
 * @param {number} x
 * @param {number} y
 * @param {Element} element
 * @returns {boolean}
 */
export function isPointWithinElement(x, y, element) {
  const { left, right, top, bottom } = element.getBoundingClientRect();
  return x >= left && x <= right && y >= top && y <= bottom;
}

/**
 * Returns focusable elements within a container.
 * @param {Element} container
 * @returns {HTMLElement[]}
 */
export function getFocusableElements(container) {
  return /** @type {HTMLElement[]} */ (
    Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter(
      (el) =>
        el instanceof HTMLElement &&
        (el.offsetParent !== null || el instanceof HTMLDialogElement)
    )
  );
}

/**
 * Traps focus within a container for keyboard navigation.
 * @param {KeyboardEvent} event
 * @param {Element} container
 */
export function trapFocus(event, container) {
  if (event.key !== 'Tab') return;

  const focusable = getFocusableElements(container);
  if (focusable.length === 0) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  if (!first || !last) return;

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

/**
 * Traps focus within a container and returns a cleanup function.
 * @param {HTMLElement} container
 * @returns {() => void}
 */
export function trapFocusContainer(container) {
  if (!container || typeof container.addEventListener !== 'function') {
    return () => {};
  }

  const focusable = getFocusableElements(container);
  if (focusable.length === 0) return () => {};

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (!first || !last) return () => {};

  const firstElement = /** @type {HTMLElement} */ (first);
  const lastElement = /** @type {HTMLElement} */ (last);

  /**
   * @param {KeyboardEvent} event
   */
  function handleKeydown(event) {
    if (event.key !== 'Tab') return;

    if (event.shiftKey) {
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else if (document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  container.addEventListener('keydown', handleKeydown);
  firstElement.focus();

  return () => {
    container.removeEventListener('keydown', handleKeydown);
  };
}

/**
 * Format milliseconds into countdown display units.
 * @param {number} ms - Milliseconds remaining
 * @returns {{ days: string, hours: string, minutes: string, seconds: string, total: number }}
 */
export function formatCountdown(ms) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  /**
   * @param {number} num
   * @returns {string}
   */
  const pad = function (num) {
    return String(num).padStart(2, '0');
  };

  return {
    days: pad(days),
    hours: pad(hours),
    minutes: pad(minutes),
    seconds: pad(seconds),
    total: totalSeconds,
  };
}

/**
 * Adds a class when elements enter the viewport.
 * @param {string} selector
 * @param {string} [className]
 * @param {IntersectionObserverInit} [options]
 */
export function animateOnView(
  selector,
  className = 'animate-in',
  options = { rootMargin: '0px', threshold: 0.1 }
) {
  if (typeof document === 'undefined') return;

  const elements = document.querySelectorAll(selector);
  if (elements.length === 0) return;

  if (!('IntersectionObserver' in window)) {
    elements.forEach((el) => el.classList.add(className));
    return;
  }

  const observer = new IntersectionObserver((entries, obs) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add(className);
        obs.unobserve(entry.target);
      }
    });
  }, options);

  elements.forEach((el) => observer.observe(el));
}

/**
 * Resolve marquee direction to a numeric movement value.
 * @param {string} direction - left or right
 * @returns {number} - -1 for left, 1 for right
 */
export function getMarqueeDirectionMultiplier(direction = 'left') {
  return String(direction).toLowerCase() === 'right' ? 1 : -1;
}

/**
 * Ensure marquee content has enough repeated sets to loop smoothly.
 * @param {HTMLElement} track - Track element that stores marquee sets
 * @param {HTMLElement} sourceSet - Original set element to clone
 * @param {number} requiredWidth - Width to cover for smooth loop
 * @returns {{ copies: number, width: number }} - Number of rendered sets and the base set width
 */
export function ensureMarqueeSetCopies(track, sourceSet, requiredWidth) {
  if (!track || !sourceSet) {
    return { copies: 0, width: 0 };
  }

  let sets = /** @type {HTMLElement[]} */ (
    Array.from(track.querySelectorAll('[data-marquee-set]'))
  );
  /** @type {HTMLElement | null} */
  let templateSet = sets[0] || null;

  if (!templateSet) {
    templateSet = sourceSet;
    track.appendChild(templateSet);
    sets = [templateSet];
  }

  templateSet.setAttribute('data-marquee-template', '');
  templateSet.removeAttribute('aria-hidden');
  templateSet.removeAttribute('data-marquee-clone');

  sets.slice(1).forEach((set) => {
    set.removeAttribute('data-marquee-template');
    set.setAttribute('data-marquee-clone', '');
    set.setAttribute('aria-hidden', 'true');
  });

  const baseWidth = templateSet.getBoundingClientRect().width;
  if (!baseWidth) {
    return { copies: 1, width: baseWidth };
  }

  const minWidth = Math.max(requiredWidth, baseWidth * 2);
  let copies = 1;

  while (baseWidth * copies < minWidth && copies < 12) {
    copies += 1;
  }

  while (sets.length < copies) {
    const clone = sourceSet.cloneNode(true);
    if (!(clone instanceof HTMLElement)) {
      break;
    }
    clone.removeAttribute('data-marquee-template');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('data-marquee-clone', '');
    track.appendChild(clone);
    sets.push(clone);
  }

  while (sets.length > copies) {
    const set = sets.pop();
    if (set) {
      set.remove();
    }
  }

  return { copies, width: baseWidth };
}

/** Canonical viewport tier widths (px). Match typography breakpoints. */
export const BREAKPOINTS = {
  mobileMax: 639,
  tabletMin: 640,
  tabletMax: 879,
  desktopMin: 880,
};

/**
 * @returns {boolean} True when viewport is mobile tier (≤639px)
 */
export function isMobile() {
  return window.matchMedia(`(max-width: ${BREAKPOINTS.mobileMax}px)`).matches;
}

/**
 * @returns {boolean} True when viewport is tablet tier (640–879px)
 */
export function isTablet() {
  return window.matchMedia(
    `(min-width: ${BREAKPOINTS.tabletMin}px) and (max-width: ${BREAKPOINTS.tabletMax}px)`
  ).matches;
}

/**
 * @returns {boolean} True when viewport is desktop tier (≥880px)
 */
export function isDesktop() {
  return window.matchMedia(`(min-width: ${BREAKPOINTS.desktopMin}px)`).matches;
}

/**
 * @returns {boolean} True when theme respects OS reduced-motion preference
 */
export function shouldReduceMotion() {
  const respectSetting =
    document.documentElement.dataset.respectReducedMotion === 'true';
  return (
    respectSetting &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}
